// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * game.ts — one player's field: towers, creeps, waves, gold, Heart.
 *
 * ── Why this is a per-peer sim and not a host-authoritative one ─────────────
 *
 * Every player has their OWN field, and nobody else's field can affect it except
 * through one narrow channel: a spore somebody sent. So each peer simulates only
 * itself. That removes the entire class of desync bug that snapshot-star netcode
 * exists to manage — there is no shared continuous state to disagree about — and
 * leaves the host owning exactly three things: the wave clock, the round seed,
 * and the win check. Which is also all a promoted host has to take over.
 *
 * A consequence worth being explicit about: creep positions do NOT need to be
 * deterministic across peers, because no other peer ever computes them. Only the
 * WAVE SCRIPT is shared (so everyone faces the same fight), and that comes from
 * the round seed via rng.ts. That is why `step()` accepts a variable dt happily
 * while `waveScript()` is a pure function of (seed, wave, mode).
 *
 * ── The targeting rule ─────────────────────────────────────────────────────
 *
 * Towers shoot the creep FURTHEST ALONG ITS ROAD among those in range — never
 * the nearest to the tower. This is not a preference, it is the difference
 * between a working game and a broken one: creeps queue single-file down a
 * corridor, so "nearest to the tower" lets the leader — the one about to reach
 * the Heart — walk past a gun that is busy shooting the creep behind it. That is
 * precisely the Scrapwall defect in Sporeline's clothing, so it is audited from
 * outside the sim at zero tolerance (see tests/balance.test.ts, misTargetedShots)
 * rather than trusted because it is written down here.
 */

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import type { Mode } from './modes';
import {
  canPlace,
  cellOf,
  colOf,
  fieldOf,
  flow,
  isReserved,
  nextStep,
  roadCells,
  rowOf,
  UNREACHABLE,
  type FieldSpec,
} from './field';

// ── towers ──────────────────────────────────────────────────────────────────

export type TowerKind = 'spitter' | 'mire' | 'lance';

export interface TowerSpec {
  kind: TowerKind;
  name: string;
  /** The shape drawn on the field. Colour is NOT the channel — see palette.ts. */
  glyph: 'disc' | 'ring' | 'chevron';
  hint: string;
  cost: number;
  /** Cost to reach level 1, then level 2. Index by current level. */
  upgrade: [number, number];
  /** Range in cells, by level. */
  range: [number, number, number];
  /** Damage per shot, by level. Zero for a tower that only slows. */
  damage: [number, number, number];
  /** Milliseconds between shots. */
  fireMs: number;
  /**
   * Speed multiplier applied to creeps in range, by level. 1 = no slow. This is
   * an AURA recomputed every tick, not a timed debuff — a creep that walks out
   * of a Mire's reach is immediately back to full speed, which is what makes
   * Mire a positional tool rather than a damage-over-time one.
   */
  slow: [number, number, number];
}

export const TOWERS: Record<TowerKind, TowerSpec> = {
  spitter: {
    kind: 'spitter',
    name: 'Spitter',
    glyph: 'disc',
    hint: 'Cheap, quick, short reach. The wall you can afford.',
    cost: 14,
    upgrade: [18, 26],
    range: [2.4, 2.7, 3.0],
    damage: [8, 14, 24],
    fireMs: 500,
    slow: [1, 1, 1],
  },
  mire: {
    kind: 'mire',
    name: 'Mire',
    glyph: 'ring',
    hint: 'Deals nothing. Slows everything in reach — so the guns get more shots.',
    cost: 20,
    upgrade: [24, 34],
    range: [2.0, 2.3, 2.6],
    damage: [0, 0, 0],
    fireMs: 500,
    slow: [0.62, 0.5, 0.38],
  },
  lance: {
    kind: 'lance',
    name: 'Lance',
    glyph: 'chevron',
    hint: 'Long reach, heavy hit, slow to reload. Covers a whole corridor.',
    cost: 34,
    upgrade: [42, 60],
    range: [4.6, 5.0, 5.4],
    damage: [50, 88, 148],
    fireMs: 900,
    slow: [1, 1, 1],
  },
};

export const TOWER_KINDS: readonly TowerKind[] = ['spitter', 'mire', 'lance'];

/** What a tower sells back for. Deliberately lossy: a maze is a commitment. */
export const SELL_REFUND = 0.6;

export interface Tower {
  cell: number;
  kind: TowerKind;
  /** 0, 1 or 2. */
  level: number;
  cooldownMs: number;
}

export function towerCost(kind: TowerKind, level: number): number {
  return TOWERS[kind].cost + TOWERS[kind].upgrade.slice(0, level).reduce((a, b) => a + b, 0);
}

export function sellValue(t: Tower): number {
  return Math.floor(towerCost(t.kind, t.level) * SELL_REFUND);
}

// ── creeps ──────────────────────────────────────────────────────────────────

export type CreepKind = 'crawler' | 'runner' | 'husk' | 'spore';

export interface CreepSpec {
  kind: CreepKind;
  /** Multiplier on the wave's base hp. */
  hp: number;
  /** Cells per second. */
  speed: number;
  /** Gold on kill. */
  gold: number;
  /** Heart beats lost if it gets through. */
  leak: number;
  /** Drawn shape and radius — the primary channel, since all four are bright. */
  shape: 'disc' | 'diamond' | 'hex' | 'star';
  radius: number;
}

export const CREEPS: Record<CreepKind, CreepSpec> = {
  crawler: { kind: 'crawler', hp: 1, speed: 1.7, gold: 4, leak: 1, shape: 'disc', radius: 0.3 },
  runner: { kind: 'runner', hp: 0.5, speed: 3.2, gold: 4, leak: 1, shape: 'diamond', radius: 0.22 },
  husk: { kind: 'husk', hp: 3.4, speed: 1.05, gold: 10, leak: 2, shape: 'hex', radius: 0.4 },
  spore: { kind: 'spore', hp: 1.5, speed: 1.9, gold: 6, leak: 1, shape: 'star', radius: 0.32 },
};

export interface Creep {
  id: number;
  kind: CreepKind;
  /** The cell it is walking out of. */
  cell: number;
  /** The cell it is walking into, or -1 when it has nowhere to go. */
  next: number;
  /** 0..1 along cell -> next. */
  t: number;
  hp: number;
  maxHp: number;
  /** Aura slow this tick, 1 when unslowed. Recomputed every step. */
  speedMul: number;
  /** True once it has been counted as a leak, so it cannot double-hit. */
  done: boolean;
}

// ── economy ─────────────────────────────────────────────────────────────────

export interface EconConfig {
  baseIncome: number;
  sendBase: number;
  sendGrowth: number;
  sendIncome: number;
  catchupPerBeat: number;
  incomeCap: number;
  hp0: number;
  hpGrowth: number;
  hpLate: number;
  countGrowth: number;
  countCap: number;
}

/**
 * Every economic and difficulty constant in one block, because balance.test.ts
 * tunes against these and a number buried in a method is a number nobody
 * re-measures.
 *
 * Deliberately MUTABLE, and only ever mutated by the balance sweep (which
 * restores it afterwards). The alternative — threading a config object through
 * Game, Match and Bot — buys nothing at runtime, because nothing in the shipped
 * game ever writes to it, and it made the sweep that produced these values much
 * harder to write. tests/balance.test.ts asserts the shipped values by name, so
 * a stray edit here turns a test red rather than silently re-balancing the game.
 */
export const ECON: EconConfig = {
  /** Gold paid at the end of every wave, before send income. */
  baseIncome: 11,
  /** Cost of the Nth spore you send: base * growth^N. */
  sendBase: 24,
  sendGrowth: 1.16,
  /** Permanent per-wave income each spore you send adds. */
  sendIncome: 4,
  /**
   * Catch-up income for a player behind on Heart beats: this much gold per beat
   * of deficit against the healthiest live player, per wave.
   *
   * Present from the first build so the sim can measure it with a control arm
   * (0) rather than being added later as a confident fix for a snowball nobody
   * measured. See balance.test.ts.
   */
  catchupPerBeat: 1.5,
  /** Hard ceiling on per-wave income, so a send rush cannot compound forever. */
  incomeCap: 60,
  /**
   * Base creep hp at wave 1, and its per-wave multiplier to the knee.
   *
   * `hp0` is the single most consequential number in the game and it was chosen
   * by measurement, not by feel. The first build had hp0 16, and the sim's
   * verdict was blunt: NOBODY lost a Heart beat before wave 15, in any mode, in
   * any of 240 matches. The leader-wins metric could not even be computed before
   * wave 12 for want of a leader. Three quarters of every match was a formality.
   *
   * The sweep that fixed it also REFUTED the obvious fix. Relaxing the late hp
   * curve alone (the `hpLate` knee below, at the original hp0) produced numbers
   * byte-identical to the broken build for the early game and simply stretched
   * matches from 20 waves to 30 — a longer formality. Only a much heavier
   * opening wave creates stakes: hp0 16 -> 42 moved the average first leak from
   * wave 15.1 to wave 4.1, and the leader-at-wave-N curve from "uncomputable"
   * to a flat 50-57% across the whole match.
   */
  hp0: 42,
  hpGrowth: 1.185,
  /**
   * A SECOND, gentler hp multiplier that takes over past `HP_KNEE`.
   *
   * On its own it does nothing for the problem above — see the hp0 note. Its
   * real job is the other end: with a single exponential every player collapsed
   * within three waves of each other regardless of how well they had played,
   * because the hp function outran every possible maze at the same moment. The
   * knee is what makes the endgame about whose corridor is better rather than
   * about which wave the exponential went vertical on.
   */
  hpLate: 1.125,
  /** Creeps per wave: 4 + wave * countGrowth, capped. */
  countGrowth: 1.35,
  countCap: 26,
};

/** Waves after which hp growth relaxes from `hpGrowth` to `hpLate`. */
export const HP_KNEE = 8;

export function sendCost(sent: number): number {
  return Math.ceil(ECON.sendBase * Math.pow(ECON.sendGrowth, sent));
}

/**
 * Base hp of a wave-N creep before its kind multiplier.
 *
 * Two regimes, not one. Steep to the knee so the opening actually threatens a
 * Heart, then gentle so the endgame is decided by whose maze is better rather
 * than by whose turn it was when the exponential went vertical.
 */
export function waveHp(wave: number): number {
  const w = Math.max(1, wave);
  if (w <= HP_KNEE) return ECON.hp0 * Math.pow(ECON.hpGrowth, w - 1);
  return ECON.hp0 * Math.pow(ECON.hpGrowth, HP_KNEE - 1) * Math.pow(ECON.hpLate, w - HP_KNEE);
}

// ── the wave script ─────────────────────────────────────────────────────────

export interface Spawn {
  kind: CreepKind;
  /** Ms after the wave starts. */
  atMs: number;
  /** Which gate it walks in from. */
  gate: number;
}

/**
 * The wave script: a pure function of (seed, wave, gateCount).
 *
 * Shared across every player in the match, because facing the same fight is what
 * makes comparing two mazes mean anything. Sent spores are added on top, per
 * field — they are the only asymmetry, and they are the point.
 */
export function waveScript(seed: number, wave: number, gates: number): Spawn[] {
  const rng: Rng = makeRng(`${seed}:w${wave}`);
  const out: Spawn[] = [];

  const count = Math.min(4 + Math.floor(wave * ECON.countGrowth), ECON.countCap);
  // Runners appear from wave 3, husks from wave 5 — so the opening is legible
  // and the player learns one creep at a time.
  const runnerOdds = wave < 3 ? 0 : Math.min(0.32, 0.06 * (wave - 2));
  const huskOdds = wave < 5 ? 0 : Math.min(0.26, 0.045 * (wave - 4));

  let at = 0;
  for (let i = 0; i < count; i++) {
    const roll = rng();
    const kind: CreepKind =
      roll < huskOdds ? 'husk' : roll < huskOdds + runnerOdds ? 'runner' : 'crawler';
    out.push({ kind, atMs: at, gate: gates === 1 ? 0 : Math.floor(rng() * gates) });
    // Husks lumber in with a gap; runners come in a rush.
    at += kind === 'husk' ? 900 : kind === 'runner' ? 260 : 520;
  }
  return out;
}

// ── events (what the sim audits) ────────────────────────────────────────────

export type GameEvent =
  | { t: 'fire'; cell: number; range: number; target: number; atMs: number; live?: CreepProbe[] }
  | { t: 'kill'; kind: CreepKind; gold: number; cell: number }
  | { t: 'leak'; kind: CreepKind; beats: number }
  | { t: 'spawn'; kind: CreepKind; id: number }
  | { t: 'place'; kind: TowerKind; cell: number; road: number }
  | { t: 'reject'; reason: string }
  | { t: 'wave'; wave: number }
  | { t: 'dead' };

/** A live creep as the AUDITOR sees it — enough to re-derive the target itself. */
export interface CreepProbe {
  id: number;
  x: number;
  y: number;
  /** Cells still to walk. Lower means further along. */
  rem: number;
}

// ── the field sim ───────────────────────────────────────────────────────────

export interface FieldStats {
  kills: number;
  leaked: number;
  goldEarned: number;
  sent: number;
  received: number;
  towersPlaced: number;
  /** The longest the road has EVER been — what the results screen scores. */
  peakRoad: number;
}

export class Game {
  readonly spec: FieldSpec;
  readonly mode: Mode;
  readonly seed: number;

  blocked: Uint8Array;
  dist: Uint16Array;
  towers = new Map<number, Tower>();
  creeps: Creep[] = [];

  gold: number;
  heart: number;
  wave = 0;
  /** Ms elapsed inside the current wave. */
  waveMs = 0;
  over = false;

  sentCount = 0;
  stats: FieldStats = {
    kills: 0,
    leaked: 0,
    goldEarned: 0,
    sent: 0,
    received: 0,
    towersPlaced: 0,
    peakRoad: 0,
  };

  events: GameEvent[] = [];
  /** When true, `fire` events carry a full live-creep probe for the auditor. */
  audit = false;

  private pending: Spawn[] = [];
  private nextId = 1;
  private scratch: Uint16Array;
  private occupied = new Set<number>();

  constructor(mode: Mode, seed: number) {
    this.mode = mode;
    this.seed = seed;
    this.spec = fieldOf(mode);
    this.blocked = new Uint8Array(this.spec.size);
    this.dist = new Uint16Array(this.spec.size);
    this.scratch = new Uint16Array(this.spec.size);
    this.gold = mode.startGold;
    this.heart = mode.heartBeats;
    this.refreshFlow();
  }

  // ── geometry ──────────────────────────────────────────────────────────────

  private refreshFlow(): void {
    flow(this.spec, this.blocked, this.dist);
    const road = roadCells(this.spec, this.dist).length;
    if (road > this.stats.peakRoad) this.stats.peakRoad = road;
  }

  roadLength(): number {
    return roadCells(this.spec, this.dist).length;
  }

  road(): number[] {
    return roadCells(this.spec, this.dist);
  }

  /**
   * True when a Gate has no route to the Heart. Must NEVER be true — the whole
   * game rests on it, so the sim audits it every tick rather than trusting
   * canPlace. See balance.test.ts, sealedFields.
   */
  isSealed(): boolean {
    for (const gate of this.spec.gates) if (this.dist[gate] === UNREACHABLE) return true;
    return false;
  }

  /** Cells a creep is standing on or crossing into — no tower may land there. */
  private occupiedCells(): Set<number> {
    this.occupied.clear();
    for (const c of this.creeps) {
      if (c.done) continue;
      this.occupied.add(c.cell);
      if (c.next >= 0) this.occupied.add(c.next);
    }
    return this.occupied;
  }

  /** Preview a placement without committing it — drives the drag ghost. */
  probe(cell: number, kind: TowerKind): { ok: boolean; reason: string; road: number } {
    const full = this.evaluate(cell, kind);
    return full.ok
      ? { ok: true, reason: 'ok', road: full.road.length }
      : { ok: false, reason: full.reason, road: this.roadLength() };
  }

  /**
   * Like `probe`, but hands back the ROAD CELLS the placement would produce.
   *
   * It exists because the bot needs both "how much longer does the road get"
   * and "how much of that road would this tower cover", and computing them
   * separately meant two breadth-first passes per candidate cell. The bot scans
   * dozens of cells per decision across four fields for hundreds of matches in
   * balance.test.ts, so that second pass was most of the sim's runtime.
   */
  evaluate(
    cell: number,
    kind: TowerKind,
  ): { ok: true; road: number[] } | { ok: false; reason: string; road: number[] } {
    if (this.gold < TOWERS[kind].cost) return { ok: false, reason: 'gold', road: [] };
    const check = canPlace(this.spec, this.blocked, cell, this.occupiedCells(), this.scratch);
    if (!check.ok) return { ok: false, reason: check.reason, road: [] };
    return { ok: true, road: roadCells(this.spec, check.dist) };
  }

  /**
   * Cells worth considering for a tower: anything touching the current road or
   * an existing tower.
   *
   * This is a real narrowing, not just an optimisation — a tower dropped in open
   * ground far from the road neither lengthens it nor covers it, so the cells
   * excluded here are exactly the ones no competent player would consider. It
   * cuts the bot's search from every free cell to a few dozen.
   */
  candidateCells(): number[] {
    const near = new Set<number>();
    const seeds = [...roadCells(this.spec, this.dist), ...this.towers.keys()];
    for (const seed of seeds) {
      const col = colOf(this.spec, seed);
      const row = rowOf(this.spec, seed);
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const nc = col + dx;
          const nr = row + dy;
          if (nc < 0 || nc >= this.spec.cols || nr < 0 || nr >= this.spec.rows) continue;
          const n = nr * this.spec.cols + nc;
          if (this.blocked[n] || isReserved(this.spec, n)) continue;
          near.add(n);
        }
      }
    }
    return [...near].sort((a, b) => a - b);
  }

  // ── actions ───────────────────────────────────────────────────────────────

  place(cell: number, kind: TowerKind): boolean {
    if (this.over) return false;
    const spec = TOWERS[kind];
    if (this.gold < spec.cost) {
      this.events.push({ t: 'reject', reason: 'gold' });
      return false;
    }
    const check = canPlace(this.spec, this.blocked, cell, this.occupiedCells(), this.scratch);
    if (!check.ok) {
      this.events.push({ t: 'reject', reason: check.reason });
      return false;
    }
    this.gold -= spec.cost;
    this.blocked[cell] = 1;
    this.towers.set(cell, { cell, kind, level: 0, cooldownMs: 0 });
    this.stats.towersPlaced++;
    this.refreshFlow();
    this.retargetCreeps();
    this.events.push({ t: 'place', kind, cell, road: this.roadLength() });
    return true;
  }

  upgrade(cell: number): boolean {
    const tower = this.towers.get(cell);
    if (this.over || !tower || tower.level >= 2) return false;
    const cost = TOWERS[tower.kind].upgrade[tower.level];
    if (this.gold < cost) {
      this.events.push({ t: 'reject', reason: 'gold' });
      return false;
    }
    this.gold -= cost;
    tower.level++;
    return true;
  }

  sell(cell: number): boolean {
    const tower = this.towers.get(cell);
    if (this.over || !tower) return false;
    this.gold += sellValue(tower);
    this.towers.delete(cell);
    this.blocked[cell] = 0;
    this.refreshFlow();
    this.retargetCreeps();
    return true;
  }

  /** A rival's spore lands. It walks in at the next tick, from a random gate. */
  receive(kind: CreepKind = 'spore'): void {
    if (this.over) return;
    this.stats.received++;
    const gate = this.spec.gates.length === 1 ? 0 : this.stats.received % this.spec.gates.length;
    this.pending.push({ kind, atMs: this.waveMs, gate });
  }

  /** Spend on a send. Returns the cost, or 0 when it could not be afforded. */
  spendSend(): number {
    const cost = sendCost(this.sentCount);
    if (this.over || this.gold < cost) return 0;
    this.gold -= cost;
    this.sentCount++;
    this.stats.sent++;
    return cost;
  }

  // ── the wave clock ────────────────────────────────────────────────────────

  startWave(n: number): void {
    if (this.over) return;
    this.wave = n;
    this.waveMs = 0;
    this.pending.push(...waveScript(this.seed, n, this.spec.gates.length));
    this.events.push({ t: 'wave', wave: n });
  }

  /**
   * Pay the end-of-wave income.
   *
   * `rivalBest` is the healthiest live rival's Heart, used for catch-up income.
   * Passing this in rather than reaching for a match object keeps Game usable
   * standalone — which is what lets the balance sim drive four of them cheaply.
   */
  payIncome(rivalBest: number): void {
    if (this.over) return;
    const deficit = Math.max(0, rivalBest - this.heart);
    const income = Math.min(
      ECON.incomeCap,
      ECON.baseIncome + this.sentCount * ECON.sendIncome + deficit * ECON.catchupPerBeat,
    );
    this.gold += income;
    this.stats.goldEarned += income;
  }

  // ── the step ──────────────────────────────────────────────────────────────

  step(dtMs: number): void {
    if (this.over) return;
    this.waveMs += dtMs;
    const dt = dtMs / 1000;

    this.spawnDue();
    this.applySlows();
    this.moveCreeps(dt);
    this.fireTowers(dtMs);
    this.reap();

    if (this.heart <= 0 && !this.over) {
      this.over = true;
      this.events.push({ t: 'dead' });
    }
  }

  private spawnDue(): void {
    if (!this.pending.length) return;
    const due = this.pending.filter((s) => s.atMs <= this.waveMs);
    if (!due.length) return;
    this.pending = this.pending.filter((s) => s.atMs > this.waveMs);
    for (const s of due) {
      const spec = CREEPS[s.kind];
      const hp = Math.max(1, Math.round(waveHp(Math.max(1, this.wave)) * spec.hp));
      const cell = this.spec.gates[Math.min(s.gate, this.spec.gates.length - 1)];
      const creep: Creep = {
        id: this.nextId++,
        kind: s.kind,
        cell,
        next: nextStep(this.spec, this.dist, cell),
        t: 0,
        hp,
        maxHp: hp,
        speedMul: 1,
        done: false,
      };
      this.creeps.push(creep);
      this.events.push({ t: 'spawn', kind: s.kind, id: creep.id });
    }
  }

  /** Mire auras. Recomputed from scratch every tick — no lingering debuffs. */
  private applySlows(): void {
    for (const c of this.creeps) c.speedMul = 1;
    for (const tower of this.towers.values()) {
      const spec = TOWERS[tower.kind];
      const mul = spec.slow[tower.level];
      if (mul >= 1) continue;
      const range = spec.range[tower.level];
      const tx = colOf(this.spec, tower.cell);
      const ty = rowOf(this.spec, tower.cell);
      for (const c of this.creeps) {
        if (c.done) continue;
        const [cx, cy] = this.creepXY(c);
        if (Math.hypot(cx - tx, cy - ty) <= range && mul < c.speedMul) c.speedMul = mul;
      }
    }
  }

  private moveCreeps(dt: number): void {
    for (const c of this.creeps) {
      if (c.done) continue;
      if (c.cell === this.spec.heart) {
        this.leak(c);
        continue;
      }
      if (c.next < 0) {
        // Nowhere to walk. canPlace makes this unreachable, but a creep frozen
        // in place would be an invisible softlock, so it re-derives every tick.
        c.next = nextStep(this.spec, this.dist, c.cell);
        if (c.next < 0) continue;
      }
      let budget = CREEPS[c.kind].speed * c.speedMul * dt;
      while (budget > 0 && !c.done) {
        const need = 1 - c.t;
        if (budget < need) {
          c.t += budget;
          budget = 0;
        } else {
          budget -= need;
          c.cell = c.next;
          c.t = 0;
          if (c.cell === this.spec.heart) {
            this.leak(c);
            break;
          }
          c.next = nextStep(this.spec, this.dist, c.cell);
          if (c.next < 0) break;
        }
      }
    }
  }

  private leak(c: Creep): void {
    if (c.done) return;
    c.done = true;
    const beats = CREEPS[c.kind].leak;
    this.heart = Math.max(0, this.heart - beats);
    this.stats.leaked++;
    this.events.push({ t: 'leak', kind: c.kind, beats });
  }

  /** Where a creep is, in cell coordinates, interpolated between cells. */
  creepXY(c: Creep): [number, number] {
    const x0 = colOf(this.spec, c.cell);
    const y0 = rowOf(this.spec, c.cell);
    if (c.next < 0) return [x0, y0];
    const x1 = colOf(this.spec, c.next);
    const y1 = rowOf(this.spec, c.next);
    return [x0 + (x1 - x0) * c.t, y0 + (y1 - y0) * c.t];
  }

  /** Cells still to walk. LOWER means further along — this is the priority. */
  remaining(c: Creep): number {
    const d = this.dist[c.cell];
    if (d === UNREACHABLE) return UNREACHABLE;
    return d - c.t;
  }

  private retargetCreeps(): void {
    for (const c of this.creeps) {
      if (c.done) continue;
      // A creep mid-stride keeps walking into the cell it already entered; only
      // its onward route is re-derived. Snapping it back would let a player
      // rewind a creep by building behind it.
      if (c.t > 0 && c.next >= 0 && this.dist[c.next] !== UNREACHABLE) continue;
      c.next = nextStep(this.spec, this.dist, c.cell);
      c.t = 0;
    }
  }

  private fireTowers(dtMs: number): void {
    for (const tower of this.towers.values()) {
      const spec = TOWERS[tower.kind];
      const damage = spec.damage[tower.level];
      tower.cooldownMs -= dtMs;
      if (damage <= 0) continue;
      if (tower.cooldownMs > 0) continue;

      const range = spec.range[tower.level];
      const tx = colOf(this.spec, tower.cell);
      const ty = rowOf(this.spec, tower.cell);

      // THE RULE: furthest along its road, among those in range. Ties break on
      // the lower id so the choice is total and reproducible.
      let target: Creep | null = null;
      let bestRem = Infinity;
      for (const c of this.creeps) {
        if (c.done || c.hp <= 0) continue;
        const [cx, cy] = this.creepXY(c);
        if (Math.hypot(cx - tx, cy - ty) > range) continue;
        const rem = this.remaining(c);
        if (rem < bestRem || (rem === bestRem && target !== null && c.id < target.id)) {
          bestRem = rem;
          target = c;
        }
      }
      if (!target) continue;

      tower.cooldownMs = spec.fireMs;
      const ev: GameEvent = {
        t: 'fire',
        cell: tower.cell,
        range,
        target: target.id,
        atMs: this.waveMs,
      };
      if (this.audit) {
        ev.live = this.creeps
          .filter((c) => !c.done && c.hp > 0)
          .map((c) => {
            const [x, y] = this.creepXY(c);
            return { id: c.id, x, y, rem: this.remaining(c) };
          });
      }
      this.events.push(ev);

      target.hp -= damage;
      if (target.hp <= 0 && !target.done) {
        target.done = true;
        const gold = CREEPS[target.kind].gold;
        this.gold += gold;
        this.stats.goldEarned += gold;
        this.stats.kills++;
        this.events.push({ t: 'kill', kind: target.kind, gold, cell: target.cell });
      }
    }
  }

  private reap(): void {
    if (this.creeps.some((c) => c.done)) this.creeps = this.creeps.filter((c) => !c.done);
  }

  /** Everything still walking. */
  liveCreeps(): number {
    return this.creeps.filter((c) => !c.done).length;
  }

  /** Nothing left to fight and nothing queued — the wave is cleared. */
  waveClear(): boolean {
    return this.pending.length === 0 && this.liveCreeps() === 0;
  }

  /** Cells that could still take a tower. Drives the bot and the "no room" hint. */
  buildableCells(): number[] {
    const out: number[] = [];
    for (let cell = 0; cell < this.spec.size; cell++) {
      if (this.blocked[cell] || isReserved(this.spec, cell)) continue;
      out.push(cell);
    }
    return out;
  }

  /** Compact tower grid for the display-only rival snapshot on the `fld` wire. */
  packField(): string {
    let s = '';
    for (let cell = 0; cell < this.spec.size; cell++) {
      const t = this.towers.get(cell);
      s += t ? String(TOWER_KINDS.indexOf(t.kind) + 1) : '0';
    }
    return s;
  }
}

/** Convenience for tests and the bot: place by column/row. */
export function placeAt(g: Game, col: number, row: number, kind: TowerKind): boolean {
  return g.place(cellOf(g.spec, col, row), kind);
}
